import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { role } from "@/features/auth/types";
import { companyDetectionSchema } from "@/features/company/schemas";
import { FullCountry } from "@/features/user/types";
import { FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { Themes } from "@/types/theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

type CompanyDetectionPrps = {
  onValidate: (data: CompanyDetection | null) => void;
  countries: FullCountry[];
  loading: boolean;
};
export type CompanyDetection = {
  inscriptionCountry?: string | null;
  inscriptionNumber?: string | null;
  idCompany?: string | null;
};

export const CompanyDetection = ({ countries, onValidate, loading }: CompanyDetectionPrps) => {
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow";
  const labelInputDivClassName = "md:flex items-center md:space-x-6";
  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = userRoles.includes(role.ADMIN);
  const { t } = useTranslation(["pass"]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      idCompany: null,
      inscriptionCountry: null,
      inscriptionNumber: null
    },
    resolver: zodResolver(companyDetectionSchema)
  });

  const onSubmit: SubmitHandler<CompanyDetection | null> = (data) => {
    if (data?.inscriptionNumber) {
      const rcs = data?.inscriptionNumber?.replace(/\s+/g, "");
      data.inscriptionNumber = rcs;
    }
    onValidate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col justify-between p-4">
        <div className="mt-8 flex w-1/2 w-full flex-row justify-center p-4">
          <Base> {t("business.title_step1")}</Base>{" "}
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-10")}>
          <Label className={labelClassName}>
            {`${watch("inscriptionNumber") ? "* " : ""}${t("business.input.registration_country")}`}{" "}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  dataCy="dropdown-inscription-country"
                  items={
                    (countries ?? []).map((country) => ({
                      key: country.alpha2,
                      value: country.defaultName,
                      icon: <Flag countryCode={country.alpha2} />,
                      render: ({ key }) => (
                        <div className={"flex w-full"} key={key}>
                          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                          <Base className={"ml-2"}>{country.defaultName}</Base>
                        </div>
                      ),
                      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                        ? "default"
                        : undefined
                    })) ?? []
                  }
                  value={value}
                  search={true}
                  autoComplete="country"
                  errorClassName={errors.inscriptionCountry && "border-red-500"}
                  className={"w-full"}
                  onChange={(value) => {
                    onChange(value.key);
                  }}
                />
              )}
              name="inscriptionCountry"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <Label className={labelClassName}>{t("business.input.number_registration")} </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  data-tooltip={t("business.input.number_registration")}
                  placeholder={"Ex: RCS, ..."}
                  disabled={!watch("inscriptionCountry")}
                  value={value ?? undefined}
                  required={false}
                  onChange={(value) => {
                    if (watch("inscriptionCountry") === "FR") {
                      const string = value.replace(/\s+/g, "");
                      const groupe = string.match(/.{1,3}/g)?.join(" ") || "";
                      onChange(groupe);
                    } else {
                      onChange(value);
                    }
                  }}
                  dataCy="input-number-registration"
                />
              )}
              name="inscriptionNumber"
            />
          </div>
        </div>
        {!isAdmin && (
          <div className="mt-8 flex w-full flex-row justify-center">
            <Base className="font-semiBold">{t("business.input.separator")}</Base>
          </div>
        )}
        {!isAdmin && (
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <Label className={labelClassName}>{t("business.input.id_olkypass")} </Label>
            <div className={inputClassName}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    data-tooltip={t("business.input.id_olkypass")}
                    placeholder={"ID"}
                    required={false}
                    error={errors.idCompany ? t("atoms:general.required") : ""}
                    value={value ?? undefined}
                    onChange={onChange}
                  />
                )}
                name="idCompany"
              />
            </div>
          </div>
        )}
        <div className="mt-8 flex w-full flex-row justify-center">
          <Button
            size="md"
            loading={loading}
            type="submit"
            theme={Themes.success}
            onClick={() => {}}
            className="w-auto rounded-lg"
          >
            {watch("idCompany") || watch("inscriptionNumber")
              ? t("business.label.validate")
              : t("business.label.next")}
          </Button>
        </div>
      </div>
    </form>
  );
};
